import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Member, MemberStatement, MemberStatus } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const STATUS_OPTIONS: MemberStatus[] = ['ACTIVE', 'DEFERRED', 'PENSIONER', 'DECEASED'];

export function StaffMembers() {
  const { persona } = useAuth();
  const canManage = persona?.roles.some((r) => ['SPF_OFFICER', 'SPF_ADMIN'].includes(r)) ?? false;
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [showRegister, setShowRegister] = useState(false);
  const [statementId, setStatementId] = useState<string | null>(null);

  const query = useMemo(() => {
    const p = new URLSearchParams({ limit: '100' });
    if (q) p.set('q', q);
    if (status) p.set('status', status);
    return `/api/v1/members?${p.toString()}`;
  }, [q, status]);
  const listQ = useApiQuery<{ items: Member[] }>(canManage ? query : null);

  if (!canManage) {
    return (
      <div>
        <PageHeader title="Members" subtitle="Member registry (P01)" />
        <ErrorNote message="Your role does not include SPF_OFFICER or SPF_ADMIN — the member registry is unavailable." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Members"
        subtitle="Register members and review contribution statements"
        actions={
          <button className="btn-primary" onClick={() => setShowRegister(true)}>
            + Register member
          </button>
        }
      />

      <div className="mb-4 flex flex-wrap gap-2">
        <input
          className="field-input max-w-xs"
          placeholder="Search by NIN or name…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select className="field-input max-w-[180px]" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No members found" hint="Register the first member, or adjust your search." />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">NIN</th>
                <th className="table-head">Full name</th>
                <th className="table-head">Date of birth</th>
                <th className="table-head">Status</th>
                <th className="table-head">Legacy SSF ref</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((m) => (
                <tr key={m.id} className="hover:bg-slate-50">
                  <td className="table-cell font-mono text-xs">{m.nin}</td>
                  <td className="table-cell font-medium">{m.fullName}</td>
                  <td className="table-cell text-slate-500">{formatDate(m.dob)}</td>
                  <td className="table-cell">
                    <StatusBadge status={m.status} />
                  </td>
                  <td className="table-cell text-xs text-slate-400">{m.legacySsfRef ?? '—'}</td>
                  <td className="table-cell text-right">
                    <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => setStatementId(m.id)}>
                      View statement
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showRegister ? (
        <RegisterModal
          onClose={() => setShowRegister(false)}
          onDone={() => {
            setShowRegister(false);
            listQ.reload();
          }}
        />
      ) : null}

      {statementId ? <StatementModal memberId={statementId} onClose={() => setStatementId(null)} /> : null}
    </div>
  );
}

function RegisterModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [form, setForm] = useState({ nin: '', fullName: '', dob: '', oneIdSub: '', legacySsfRef: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/members', {
        nin: form.nin,
        fullName: form.fullName,
        dob: form.dob,
        oneIdSub: form.oneIdSub || undefined,
        legacySsfRef: form.legacySsfRef || undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to register member');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Register a member" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="NIN">
          <input className="field-input" value={form.nin} onChange={(e) => setForm({ ...form, nin: e.target.value })} placeholder="e.g. 976-0117-2-6" />
        </Field>
        <Field label="Full name">
          <input className="field-input" value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
        </Field>
        <Field label="Date of birth">
          <input className="field-input" type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} />
        </Field>
        <Field label="Legacy SSF reference (optional)">
          <input
            className="field-input"
            value={form.legacySsfRef}
            onChange={(e) => setForm({ ...form, legacySsfRef: e.target.value })}
            placeholder="Pre-1979 Social Security Fund reference, if any"
          />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !form.nin || !form.fullName || !form.dob}>
            {busy ? 'Registering…' : 'Register member'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function StatementModal({ memberId, onClose }: { memberId: string; onClose: () => void }) {
  const statementQ = useApiQuery<MemberStatement>(`/api/v1/members/${memberId}/statement`);

  return (
    <Modal title="Contribution statement" onClose={onClose} wide>
      {statementQ.loading ? (
        <Spinner />
      ) : statementQ.error ? (
        <ErrorNote message={statementQ.error} />
      ) : statementQ.data ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-slate-900">{statementQ.data.member.fullName}</div>
              <div className="font-mono text-xs text-slate-500">{statementQ.data.member.nin}</div>
            </div>
            <StatusBadge status={statementQ.data.member.status} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="card px-3 py-2.5">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total contributed</div>
              <div className="text-lg font-bold tabular-nums">{scr(statementQ.data.totals.totalContrib)}</div>
            </div>
            <div className="card px-3 py-2.5">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Years of contribution</div>
              <div className="text-lg font-bold tabular-nums">{statementQ.data.totals.yearsOfContribution}</div>
            </div>
          </div>

          <div>
            <h3 className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">Contribution years</h3>
            {statementQ.data.years.length === 0 ? (
              <p className="text-xs text-slate-400">None posted yet.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr>
                    <th className="table-head">Year</th>
                    <th className="table-head text-right">Months</th>
                    <th className="table-head text-right">Wages</th>
                    <th className="table-head text-right">Contributed</th>
                  </tr>
                </thead>
                <tbody>
                  {statementQ.data.years.map((y) => (
                    <tr key={y.year}>
                      <td className="table-cell">{y.year}</td>
                      <td className="table-cell text-right">{y.months}</td>
                      <td className="table-cell text-right tabular-nums">{scr(y.totalWages)}</td>
                      <td className="table-cell text-right tabular-nums">{scr(y.totalContrib)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div>
            <h3 className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">Membership events</h3>
            {statementQ.data.events.length === 0 ? (
              <p className="text-xs text-slate-400">None recorded.</p>
            ) : (
              <ul className="space-y-1 text-xs text-slate-600">
                {statementQ.data.events.map((e, i) => (
                  <li key={i}>
                    {formatDate(e.date)} — {e.type.replaceAll('_', ' ')}
                    {e.employerId ? ` (${e.employerId})` : ''}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {statementQ.data.nomination ? (
            <div>
              <h3 className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">Beneficiary nomination</h3>
              <ul className="space-y-1 text-xs text-slate-600">
                {statementQ.data.nomination.persons.map((p, i) => (
                  <li key={i}>
                    {p.name} — {p.relationship} — {p.sharePercent}%
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}
